import {
  AdditionalTax,
  AddressField,
  DiscountSettings,
  IInvoiceAttachmentRequest,
  IInvoiceServiceCharge,
  Surcharge
} from 'containers/invoices/invoices.model';
import { CreateJobErrorTypes, JobStatus, JobsStatuses, ScheduleTime, createJobErrors } from './constant';
import { store } from 'store/store';
import {
  calculateInvoiceSubtotalAndNewDiscount,
  prepareLineItemList,
  prepareServiceChargeListRequestPayload,
  prepareSummatedUniqueTaxList
} from 'containers/invoices/utils';
import { DiscountFormData } from 'containers/invoices/types';
import { AmountFilterOperation, DISCOUNT_TYPE } from 'constants/common';
import { addJobValidationError } from './create-edit-job/createJobSlice';
import { InvoiceValidationError } from 'containers/invoices/create-invoice/types';
import isEqual from 'lodash/isEqual';
import { CreateJobReqBody, JobCustomer } from './jobs.model';
import DateUtils from 'utils/dateUtils';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { Tag } from 'containers/tags/tags.model';
import {
  InvoiceLineItem,
  calculateSurchargeInCurrency,
  calculateSurchargeInCurrencyWithBps,
  getLineItemsTotal,
  hasServiceCharge,
  updateServiceChargeWithBaseAmount
} from 'containers/invoices/InvoicesSlice';
import { initServiceCharge } from 'containers/invoices/create-invoice/constants';
import { AbstractITax } from 'containers/product-catalog/taxes/taxes.model';
import {
  ArchivedStatusIcon,
  CancelledStatusIcon,
  ConfirmedStatusIcon,
  DonePendingApprovalStatusIcon,
  DoneStatusIcon,
  InProgressStatusIcon,
  InboxStatusIcon,
  PendingStatusIcon,
  SubmittedStatusIcon
} from 'assets/icons/job-statuses-icon-in-map';
import {
  getAmountQueryParam,
  getDateRangeFromSchedulePreset,
  prepareEndDateForSave,
  prepareStartDateForSave,
  reverseAmountQueryParam
} from 'containers/dispatch/utils';
import { add, addWeeks, set, startOfWeek } from 'date-fns';

export const getRelativeTimeDisplay = (utcTime: string): string => {
  if (!utcTime) return '';

  try {
    const now = new Date();
    const localDate = DateUtils.parseISODateString(utcTime);

    if (isNaN(localDate.getTime())) return '';

    const daysDiff = DateUtils.getDifferenceInDays({ primaryDate: now, secondaryDate: localDate });

    const relativeString = DateUtils.fromNowRelativeString(localDate, now.getTime());

    if (relativeString === 'just now') {
      return 'Just now';
    }

    if (relativeString.includes('minute')) {
      const minutes = Math.abs(Math.round((now.getTime() - localDate.getTime()) / (1000 * 60)));
      return `${minutes} minutes ago`;
    }

    // For same day (today)
    if (daysDiff === 0) {
      const timeFormatted = DateUtils.formatTime(utcTime);
      return `Today at ${timeFormatted}`;
    }

    // For yesterday
    if (daysDiff === 1) {
      const timeFormatted = DateUtils.formatTime(utcTime);
      return `Yesterday at ${timeFormatted}`;
    }

    // For 2-6 days ago
    if (daysDiff >= 2 && daysDiff <= 6) {
      const dateStr = DateUtils.getDateInFormat({ date: localDate, dateFormat: 'yyyy-MM-dd' });
      const dayOfWeek = DateUtils.getDayOfWeek(dateStr);
      const timeFormatted = DateUtils.formatTime(utcTime);
      return `${dayOfWeek} at ${timeFormatted}`;
    }

    // For 7-13 days ago (last week)
    if (daysDiff >= 7 && daysDiff <= 13) {
      const dateStr = DateUtils.getDateInFormat({ date: localDate, dateFormat: 'yyyy-MM-dd' });
      const dayOfWeek = DateUtils.getDayOfWeek(dateStr);
      const timeFormatted = DateUtils.formatTime(utcTime);
      return `Last ${dayOfWeek} at ${timeFormatted}`;
    }

    // For same year (14+ days ago)
    if (localDate.getFullYear() === now.getFullYear()) {
      const monthDay = DateUtils.getDateInFormat({ date: localDate, dateFormat: 'MMM d' });
      const timeFormatted = DateUtils.formatTime(utcTime);
      return `${monthDay} at ${timeFormatted}`;
    }

    // For previous years
    const fullDate = DateUtils.getDateInFormat({ date: localDate, dateFormat: 'd MMM yyyy' });
    const timeFormatted = DateUtils.formatTime(utcTime);
    return `${fullDate}, ${timeFormatted}`;
  } catch (error) {
    return '';
  }
};

function getJobStatusColor(status: JobStatus) {
  switch (status) {
    case JobStatus.ARCHIVED:
      return '#828282';
    case JobStatus.DONE_PENDING_APPROVAL:
      return '#BD68DC';
    case JobStatus.PENDING:
      return '#DBAC00';
    case JobStatus.IN_PROGRESS:
      return '#F88A29';
    case JobStatus.DONE:
      return '#27AE60';
    case JobStatus.CONFIRMED:
      return '#418DFF';
    case JobStatus.SUBMITTED:
      return '#00ABB8';
    case JobStatus.INBOX:
      return '#3E556D';
    case JobStatus.CANCELED:
    case JobStatus.REJECTED:
      return '#D95945';
    default:
      return '#828282';
  }
}

function getJobStatusText(status: string) {
  switch (status) {
    case JobStatus.REJECTED:
      return 'Rejected';
    case JobStatus.SUBMITTED:
      return 'Submitted';
    case JobStatus.CONFIRMED:
      return 'Confirmed';
    case JobStatus.IN_PROGRESS:
      return 'In progress';
    case JobStatus.DONE:
      return 'Done';
    case JobStatus.PENDING:
      return 'Pending';
    case JobStatus.DONE_PENDING_APPROVAL:
      return 'Done pending approval';
    case JobStatus.CANCELED:
      return 'Canceled';
    case JobStatus.ARCHIVED:
      return 'Archived';
    case JobStatus.INBOX:
      return 'Inbox';
    default:
      return status;
  }
}

export const getJobStatusIconForMapCard = (status: JobStatus) => {
  const dimension = 'h-[15px] w-[15px] shrink-0';
  switch (status) {
    case JobStatus.INBOX:
      return <InboxStatusIcon className={dimension} />;
    case JobStatus.SUBMITTED:
      return <SubmittedStatusIcon className={dimension} />;
    case JobStatus.CONFIRMED:
      return <ConfirmedStatusIcon className={dimension} />;
    case JobStatus.IN_PROGRESS:
      return <InProgressStatusIcon className={dimension} />;
    case JobStatus.DONE_PENDING_APPROVAL:
      return <DonePendingApprovalStatusIcon className={dimension} />;
    case JobStatus.DONE:
      return <DoneStatusIcon className={dimension} />;
    case JobStatus.PENDING:
      return <PendingStatusIcon className={dimension} />;
    case JobStatus.CANCELED:
    case JobStatus.REJECTED:
      return <CancelledStatusIcon className={dimension} />;
    case JobStatus.ARCHIVED:
      return <ArchivedStatusIcon className={dimension} />;
    default:
      return <SubmittedStatusIcon className={dimension} />;
  }
};

export const getJobStatusIconForGroupedJobs = (status: JobStatus) => {
  const dimension = 'h-[15px] w-[15px] shrink-0';
  switch (status) {
    case JobStatus.INBOX:
      return <InboxStatusIcon className={dimension} />;
    case JobStatus.SUBMITTED:
      return <SubmittedStatusIcon className={dimension} />;
    case JobStatus.CONFIRMED:
      return <ConfirmedStatusIcon className={dimension} />;
    case JobStatus.IN_PROGRESS:
      return <InProgressStatusIcon className={dimension} />;
    case JobStatus.DONE_PENDING_APPROVAL:
      return <DonePendingApprovalStatusIcon className={dimension} />;
    case JobStatus.DONE:
      return <DoneStatusIcon className={dimension} />;
    case JobStatus.PENDING:
      return <PendingStatusIcon className={dimension} />;
    case JobStatus.CANCELED:
    case JobStatus.REJECTED:
      return <CancelledStatusIcon className={dimension} />;
    case JobStatus.ARCHIVED:
      return <ArchivedStatusIcon className={dimension} />;
    default:
      return <SubmittedStatusIcon className={dimension} />;
  }
};

const getAddressAsString = (address: AddressField) => {
  const { apartment, street, city, state, country, zipCode } = address ?? {};
  let addressString = '';
  if (street && street !== '') {
    addressString += ` ${street},`;
  }
  if (apartment && apartment !== '') {
    addressString += ` ${apartment},`;
  }

  if (city && city !== '') {
    addressString += ` ${city},`;
  }

  if (state && state !== '') {
    addressString += ` ${state},`;
  }

  if (country && country !== '') {
    addressString += ` ${country},`;
  }

  if (zipCode && zipCode?.length > 0) {
    addressString += ` ${zipCode}`;
  }
  return addressString;
};

export const prepareDiscountPayloadForCreateJob = (
  discountData: DiscountFormData,
  discountSettings: DiscountSettings
) => {
  if (discountSettings.enabled) {
    if (discountData.isDiscountAmountSelected) {
      return {
        discount: discountData.discountAmount,
        discountType: DISCOUNT_TYPE.FLAT
      };
    }
    return {
      discount: discountData.discountPercent,
      discountType: DISCOUNT_TYPE.PERCENTAGE
    };
  }
  return {
    discount: null,
    discountType: DISCOUNT_TYPE.FLAT
  };
};

export const getAttachmentArray = attachments => {
  const attachmentArray: IInvoiceAttachmentRequest[] = attachments.map(attachment => {
    return {
      title: attachment.title,
      url: attachment.url,
      description: '',
      source: attachment.source
    };
  });
  return attachmentArray;
};

const parseJobCustomer = (jobCustomer: JobCustomer, serviceAddress: AddressField, billingAddress: AddressField) => {
  if (!jobCustomer) {
    return null;
  }
  const { ID, ...customerWithoutID } = jobCustomer;
  return {
    ...customerWithoutID,
    id: ID || '', // map ID to id
    billingAddress: billingAddress
      ? {
          id: billingAddress.id || 0,
          type: billingAddress.type || '',
          street: billingAddress.street || '',
          apartment: billingAddress.apartment || '',
          city: billingAddress.city || '',
          state: billingAddress.state || '',
          zipCode: billingAddress.zipCode || '',
          country: billingAddress.country || '',
          addressNote: billingAddress.addressNote || '',
          latitude: billingAddress.latitude || 0,
          longitude: billingAddress.longitude || 0,
          line2: billingAddress.line2 || ''
        }
      : null,
    serviceAddress: serviceAddress
      ? [{ ...serviceAddress, latitude: serviceAddress.latitude || 0, longitude: serviceAddress.longitude || 0 }]
      : []
  };
};

export const prepareCreateJobPayload = ({
  isFromEdit = false,
  isAutoFilled = false
}: {
  isFromEdit?: boolean;
  isAutoFilled?: boolean;
}) => {
  const { rootReducer } = store.getState();
  const { createJob } = rootReducer;
  const {
    jobTitle,
    jobAttachments,
    jobCustomer,
    jobDescription,
    leadSource,
    leadSourceType,
    serviceAddress,
    isJobScheduled,
    scheduledFrom,
    scheduledTo,
    jobNotes,
    jobReferenceNumber,
    jobLineItems,
    discountFormData,
    currentSurcharge,
    isServiceFeeEnabled,
    appliedLineItemLevelTaxes,
    selectedServiceCharge,
    assigneeList,
    discountSettings,
    isFromEstimate,
    estimateId,
    estimateNumber,
    externalCompany,
    billingAddress,
    serviceArea,
    autofillKey,
    autoFilledData,
    selectedTags
  } = createJob;

  const serviceChargePayload = prepareServiceChargeListRequestPayload(selectedServiceCharge);
  const modifiedFields = autoFilledData && getModifiedAutoFilledFields();
  const payload = {
    title: jobTitle?.trim(),
    attachments: getAttachmentArray(jobAttachments),
    description: jobDescription?.trim(),
    ...(isFromEdit
      ? {
          customerId: jobCustomer['ID'],
          customerServiceAddressId: serviceAddress.id
        }
      : { customer: parseJobCustomer(jobCustomer as JobCustomer, serviceAddress, billingAddress) }),

    leadSource: leadSource?.trim(),
    leadSourceType: leadSourceType?.id,
    isScheduled: isJobScheduled,
    scheduledFrom: isJobScheduled ? scheduledFrom : null,
    scheduledTo: isJobScheduled ? scheduledTo : null,
    jobNote: { comment: jobNotes?.trim() },
    ReferenceNo: jobReferenceNumber?.trim(),
    lineItems: prepareLineItemList(jobLineItems),
    ...prepareDiscountPayloadForCreateJob(discountFormData, discountSettings),
    surchargeAmount: isServiceFeeEnabled ? currentSurcharge.valueInCurrencyWithBps?.toString() : '0',
    isServiceFeeEnabled: isServiceFeeEnabled,
    ...prepareSummatedUniqueTaxList({
      appliedLineItemLevelTaxes: appliedLineItemLevelTaxes,
      invoiceLineItems: jobLineItems,
      selectedServiceCharge: selectedServiceCharge
    }),
    serviceCharges: serviceChargePayload,
    serviceChargeAmount: serviceChargePayload?.length > 0 ? serviceChargePayload[0].totalAmount : '0',
    assignees: assigneeList?.map(assignee => assignee?.merchantId),
    ...(isFromEstimate
      ? {
          relativeEntity: {
            entityType: 'ESTIMATE',
            entityId: estimateId,
            entityNumber: estimateNumber
          }
        }
      : {}),
    externalCompanyId: externalCompany?.id,
    isAutoFilled: isAutoFilled ?? false,
    ...(isAutoFilled ? { autofillKey, isAutofillModified: modifiedFields?.length > 0 } : {}),

    serviceAreaId: serviceArea?.serviceAreaId,
    tags: selectedTags?.map(tag => tag.id) || []
  };

  return payload as CreateJobReqBody;
};

export const hasChangesToSyncInJob = (): boolean => {
  const { rootReducer } = store.getState();
  const { createJob } = rootReducer;
  const {
    jobCustomer,
    initialJobCustomer,
    serviceAddress,
    initialServiceAddress,
    billingAddress,
    initialBillingAddress,
    jobLineItems,
    initialJobLineItems,
    selectedServiceCharge,
    initialServiceCharge,
    jobSubTotal,
    initialSubTotal,
    isServiceFeeEnabled,
    isServiceFeeEnabledInitial,
    currentSurcharge,
    initialSurcharge
  } = createJob;
  const tempJobCustomer = { ...jobCustomer, createdAt: '', updatedAt: '', createdBy: '', updatedBy: '' };
  const tempInitialJobCustomer = { ...initialJobCustomer, createdAt: '', updatedAt: '', createdBy: '', updatedBy: '' };

  if (!isEqual(tempJobCustomer, tempInitialJobCustomer)) {
    return true;
  }
  if (!isEqual(serviceAddress, initialServiceAddress)) {
    return true;
  }
  if (!isEqual(billingAddress, initialBillingAddress)) {
    return true;
  }
  if (!isEqual(jobLineItems, initialJobLineItems)) {
    return true;
  }
  if (!isEqual(selectedServiceCharge, initialServiceCharge)) {
    return true;
  }
  if (jobSubTotal !== initialSubTotal) {
    return true;
  }
  if (
    !(
      isServiceFeeEnabled === isServiceFeeEnabledInitial &&
      (isServiceFeeEnabledInitial ? isEqual(currentSurcharge, initialSurcharge) : true)
    )
  ) {
    return true;
  }
  return false;
};

export const checkIfJobValuesAreValid = () => {
  const { rootReducer } = store.getState();
  const { createJob } = rootReducer;
  const {
    jobTitle,
    jobCustomer,
    leadSourceType,
    leadSource,
    externalCompany,
    serviceAddress,
    isJobScheduled,
    scheduledFrom,
    scheduledTo,
    autoFilledData
  } = createJob;
  let isValid = true;
  const errors: InvoiceValidationError[] = [];

  // Special validation for autofilled case
  const isAutoFilledCustomer = autoFilledData?.customerLatest && !autoFilledData.customerLatest.id;
  const isAutoFilledServiceAddress =
    autoFilledData?.customerLatest?.serviceAddress && !autoFilledData?.customerLatest?.serviceAddress?.[0]?.id;
  const isAutoFilledExternalCompany = autoFilledData?.externalCompany?.name;

  if (isAutoFilledCustomer || isAutoFilledServiceAddress || isAutoFilledExternalCompany) {
    if (!jobCustomer?.name) {
      isValid = false;
      const error = { type: CreateJobErrorTypes.CUSTOMER, errorMsg: createJobErrors[CreateJobErrorTypes.CUSTOMER] };
      store.dispatch(addJobValidationError(error));
      errors.push(error);
    }
    if (!serviceAddress) {
      isValid = false;
      const error = {
        type: CreateJobErrorTypes.SERVICE_ADDRESS,
        errorMsg: createJobErrors[CreateJobErrorTypes.SERVICE_ADDRESS]
      };
      store.dispatch(addJobValidationError(error));
      errors.push(error);
    }

    if (isAutoFilledServiceAddress) {
      if (
        !serviceAddress?.street ||
        !serviceAddress.city ||
        !serviceAddress.state ||
        !serviceAddress.zipCode ||
        !serviceAddress.country ||
        !serviceAddress.addressName
      ) {
        isValid = false;
        const error = {
          type: CreateJobErrorTypes.SERVICE_ADDRESS,
          errorMsg: createJobErrors[CreateJobErrorTypes.SERVICE_ADDRESS_MISSING_FIELDS]
        };
        store.dispatch(addJobValidationError(error));
        errors.push(error);
      }
    }
    if (isAutoFilledExternalCompany) {
      if (externalCompany.name && !externalCompany.id) {
        isValid = false;
        const error = {
          type: CreateJobErrorTypes.CREATE_EXTERNAL_COMPANY,
          errorMsg: createJobErrors[CreateJobErrorTypes.CREATE_EXTERNAL_COMPANY]
        };
        store.dispatch(addJobValidationError(error));
        errors.push(error);
      }
    }
  } else {
    // Normal validation for customer
    if (!jobCustomer || !jobCustomer['ID']) {
      isValid = false;
      const error = { type: CreateJobErrorTypes.CUSTOMER, errorMsg: createJobErrors[CreateJobErrorTypes.CUSTOMER] };
      store.dispatch(addJobValidationError(error));
      errors.push(error);
    }
    // Normal validation for service address
    if (!serviceAddress || !serviceAddress.id) {
      isValid = false;
      const error = {
        type: CreateJobErrorTypes.SERVICE_ADDRESS,
        errorMsg: createJobErrors[CreateJobErrorTypes.SERVICE_ADDRESS]
      };
      store.dispatch(addJobValidationError(error));
      errors.push(error);
    }
  }

  if (!jobTitle.trim()) {
    isValid = false;
    const error = { type: CreateJobErrorTypes.JOB_TITLE, errorMsg: createJobErrors[CreateJobErrorTypes.JOB_TITLE] };
    store.dispatch(addJobValidationError(error));
    errors.push(error);
  }
  // if (!leadSourceType?.id && !externalCompany) {
  //   isValid = false;
  //   const error = {
  //     type: CreateJobErrorTypes.JOB_SOURCE,
  //     errorMsg: 'Please select either Lead source or External company'
  //   };
  //   store.dispatch(addJobValidationError(error));
  //   errors.push(error);
  // }
  // if (leadSourceType?.id && [LeadSource.REFERRAL, LeadSource.OTHER].includes(leadSourceType.id) && !leadSource) {
  //   isValid = false;
  //   const error = {
  //     type: CreateJobErrorTypes.JOB_SOURCE,
  //     errorMsg: createJobErrors[CreateJobErrorTypes.LEAD_SOURCE]
  //   };
  //   store.dispatch(addJobValidationError(error));
  //   errors.push(error);
  // }
  if (isJobScheduled && (!scheduledFrom || !scheduledTo)) {
    isValid = false;
    const error = {
      type: CreateJobErrorTypes.SCHEDULE_DATE,
      errorMsg: createJobErrors[CreateJobErrorTypes.SCHEDULE_DATE]
    };
    store.dispatch(addJobValidationError(error));
    errors.push(error);
  }
  if (isJobScheduled && new Date(scheduledFrom) >= new Date(scheduledTo)) {
    isValid = false;
    const error = {
      type: CreateJobErrorTypes.SCHEDULE_DATE,
      errorMsg: createJobErrors[CreateJobErrorTypes.SCHEDULE_TIME]
    };
    store.dispatch(addJobValidationError(error));
    errors.push(error);
  }
  return { isValid, errors };
};

export const getJobValidationErrors = (validations, type: CreateJobErrorTypes) => {
  const error = validations.find(item => item.type === type);
  return error?.errorMsg || '';
};

export const getBorderStyles = ({ isSelected = false, hasError = false }) => {
  if (hasError) return 'border-error';
  if (isSelected) return 'border-secondary';
  return 'border-borderGray';
};

export { getJobStatusColor, getJobStatusText, getAddressAsString };

export const getJobViewFilters = (viewFilters: any) => {
  const {
    status,
    assignees,
    customerIds,
    leadSource,
    amount,
    createdBy,
    scheduledFrom,
    scheduledTo,
    externalCompany,
    isToday,
    schedulePreset,
    tags,
    isUnscheduled,
    serviceArea,
    invoiceStatus,
    estimateStatus,
    createdFrom,
    createdTo,
    noTags
  } = viewFilters ?? {};
  const filters: Record<string, string[] | string | number | AmountFilter | boolean | null> = {};
  try {
    // Handle special date filtering cases
    if (isToday) {
      const today = new Date();
      filters.scheduledFrom = DateUtils.getUTCStartDate(today);
      filters.scheduledTo = DateUtils.getUTCEndDate(today);
    } else if (schedulePreset) {
      const dateRange = getDateRangeFromSchedulePreset(schedulePreset);
      if (dateRange.startDate && dateRange.endDate) {
        filters.scheduledFrom = DateUtils.getUTCStartDate(dateRange.startDate);
        filters.scheduledTo = DateUtils.getUTCEndDate(dateRange.endDate);
      }
    }
    filters.schedulePreset = schedulePreset;
    filters.isToday = isToday;

    if (status) filters.status = status.split(',') as JobStatus[];
    if (assignees) filters.assignees = assignees.split(',');
    if (customerIds) filters.customerIds = customerIds.split(',');
    if (leadSource) filters.leadSource = leadSource.split(',');
    if (amount) filters.amount = reverseAmountQueryParam(amount);
    if (createdBy) filters.createdBy = createdBy.split(',');
    if (scheduledFrom && !isToday && !schedulePreset) filters.scheduledFrom = scheduledFrom;
    if (scheduledTo && !isToday && !schedulePreset) filters.scheduledTo = scheduledTo;
    if (externalCompany) filters.externalCompany = externalCompany.split(',');
    if (tags) filters.tags = tags.split(',').map(Number);
    if (noTags) filters.noTags = noTags;
    if (isUnscheduled) filters.isUnscheduled = isUnscheduled;
    if (serviceArea) filters.serviceArea = serviceArea.split(',');
    if (invoiceStatus) filters.invoiceStatus = invoiceStatus.split(',');
    if (estimateStatus) filters.estimateStatus = estimateStatus.split(',');
    if (createdFrom) filters.createdFrom = createdFrom;
    if (createdTo) filters.createdTo = createdTo;
    return filters;
  } catch (error) {
    return {};
  }
};

export const getSortedJobTags = (jobTags: Tag[]) => {
  return [...(jobTags || [])].sort((a, b) => a.priority - b.priority);
};

export const getServiceChargePayload = (
  newServiceCharge: IInvoiceServiceCharge,
  jobSubTotal: number,
  jobLineItems: InvoiceLineItem[],
  currentServiceCharge: IInvoiceServiceCharge,
  appliedLineItemLevelTaxes: AdditionalTax[],
  currentSurchargeState: Surcharge
) => {
  let surchargeInCurrencyWithBps = 0;
  let updatedServiceCharge = { ...newServiceCharge };
  let selectedAppliedLineItemLevelTaxes = [...appliedLineItemLevelTaxes];
  if (hasServiceCharge(updatedServiceCharge)) {
    const { updatedServiceCharge: updatedServiceChargeWithBaseAmount } = updateServiceChargeWithBaseAmount({
      serviceCharge: updatedServiceCharge,
      subtotal: jobSubTotal
    });
    updatedServiceCharge = updatedServiceChargeWithBaseAmount;
  }
  const serviceChargePayload = prepareServiceChargeListRequestPayload(updatedServiceCharge);
  const serviceChargeAmount = serviceChargePayload?.length > 0 ? serviceChargePayload[0].totalAmount : '0';

  const taxesInNewServiceCharge: AbstractITax[] = newServiceCharge?.taxes ?? [];
  const taxesInCurrentServiceCharge: AbstractITax[] = currentServiceCharge?.taxes ?? [];

  if (
    (currentServiceCharge === initServiceCharge ||
      currentServiceCharge === null ||
      taxesInCurrentServiceCharge.length === 0) &&
    taxesInNewServiceCharge.length > 0
  ) {
    taxesInNewServiceCharge.forEach(serviceChargeTax => {
      const isLineItemTaxAlreadySelected = selectedAppliedLineItemLevelTaxes.find(
        tax => tax.id === serviceChargeTax.id
      );
      if (!isLineItemTaxAlreadySelected) {
        selectedAppliedLineItemLevelTaxes = [...selectedAppliedLineItemLevelTaxes, serviceChargeTax as AdditionalTax];
      }
    });
  } else {
    if (taxesInCurrentServiceCharge.length > 0 && taxesInNewServiceCharge.length > 0) {
      // Remove the taxes which were added because of the current service charge
      let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = [...selectedAppliedLineItemLevelTaxes];
      taxesInCurrentServiceCharge.forEach(serviceChargeTax => {
        const isTaxAppliedOnAnyLineItem = jobLineItems.find(lineItem => {
          return lineItem?.taxes?.find(tax => tax.id === serviceChargeTax.id);
        });
        if (!isTaxAppliedOnAnyLineItem) {
          modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
            tax => tax.id !== serviceChargeTax.id
          );
        }
      });

      //  add the taxes which are in the new service charge
      taxesInNewServiceCharge.forEach(serviceChargeTax => {
        const isLineItemTaxAlreadySelected = modifiedAppliedLineItemLevelTaxes.find(
          tax => tax.id === serviceChargeTax.id
        );
        if (!isLineItemTaxAlreadySelected) {
          modifiedAppliedLineItemLevelTaxes = [...modifiedAppliedLineItemLevelTaxes, serviceChargeTax as AdditionalTax];
        }
      });
      selectedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
    } else if (taxesInCurrentServiceCharge.length > 0 && taxesInNewServiceCharge.length === 0) {
      // Remove the taxes which were added because of the current service charge
      let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = [...selectedAppliedLineItemLevelTaxes];
      taxesInCurrentServiceCharge.forEach(serviceChargeTax => {
        const isTaxAppliedOnAnyLineItem = jobLineItems.find(lineItem => {
          return lineItem?.taxes?.find(tax => tax.id === serviceChargeTax.id);
        });
        if (!isTaxAppliedOnAnyLineItem) {
          modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
            tax => tax.id !== serviceChargeTax.id
          );
        }
      });
      selectedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
    }
  }

  if (currentSurchargeState.enabled) {
    const surchargeInCurrency: number = calculateSurchargeInCurrency(currentSurchargeState.percentage, jobSubTotal);

    surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
      surchargeInCurrency,
      currentSurchargeState.basePoints
    );
  }

  return {
    serviceCharges: serviceChargePayload,
    serviceChargeAmount,
    ...prepareSummatedUniqueTaxList({
      appliedLineItemLevelTaxes: selectedAppliedLineItemLevelTaxes,
      invoiceLineItems: jobLineItems,
      selectedServiceCharge: updatedServiceCharge
    }),
    surchargeAmount: surchargeInCurrencyWithBps.toString(),
    isServiceFeeEnabled: currentSurchargeState.enabled
  };
};

export const getLineItemPayload = (
  lineItem: InvoiceLineItem,
  selectedAppliedLineItemLevelTaxes: AdditionalTax[],
  jobLineItemsState: InvoiceLineItem[],
  discountFormDataState: DiscountFormData,
  currentSurchargeState: Surcharge,
  selectedServiceCharge: IInvoiceServiceCharge,
  type: 'remove' | 'edit' | 'add'
) => {
  let surchargeInCurrencyWithBps = 0;
  let lineItemToUpdate: InvoiceLineItem = null;

  let updatedLineItemsList = jobLineItemsState.map(lineItemCurr => {
    if (lineItemCurr.id === lineItem.id) {
      lineItemToUpdate = lineItem;

      if (type !== 'edit') {
        return lineItemCurr;
      }

      return {
        ...lineItemCurr,
        itemDescription: lineItem.itemDescription,
        unitCost: lineItem.unitCost,
        originalUnitCost: lineItem.originalUnitCost,
        markupPercentage: lineItem.markupPercentage,
        quantity: lineItem.quantity,
        total: +lineItem.unitCost * +lineItem.quantity,
        additionalDetails: lineItem.additionalDetails,
        ...(lineItem?.taxes ? { taxes: lineItem.taxes } : { taxes: [] })
      };
    }
    return lineItemCurr;
  });

  if (type === 'add') {
    updatedLineItemsList = [...updatedLineItemsList, lineItem];
  }

  if (type === 'remove') {
    updatedLineItemsList = updatedLineItemsList.filter(lineItemCurr => lineItemCurr.id !== lineItem.id);
  }

  const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);

  const { subtotal } = calculateInvoiceSubtotalAndNewDiscount({
    totalCostOfItems: totalCostOfLineItems,
    discountData: discountFormDataState
  }) ?? { subtotal: totalCostOfLineItems, discountData: discountFormDataState };
  if (currentSurchargeState.enabled) {
    const surchargeInCurrency: number = calculateSurchargeInCurrency(currentSurchargeState.percentage, subtotal);

    surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
      surchargeInCurrency,
      currentSurchargeState.basePoints
    );
  }

  // update applied line item taxes array - currently selected line item level taxes
  const itemLevelTaxesInPayload = lineItem?.taxes ?? [];
  const taxesInItemBeforeUpdate = lineItemToUpdate?.taxes ?? [];
  let appliedLineItemLevelTaxes = [...selectedAppliedLineItemLevelTaxes];

  if (itemLevelTaxesInPayload.length > 0 && type !== 'remove') {
    itemLevelTaxesInPayload.forEach(itemLevelTaxInPayload => {
      const isLineItemTaxAlreadySelected = appliedLineItemLevelTaxes.find(tax => tax.id === itemLevelTaxInPayload.id);
      if (!isLineItemTaxAlreadySelected) {
        appliedLineItemLevelTaxes = [...appliedLineItemLevelTaxes, itemLevelTaxInPayload as AdditionalTax];
      }
    });
  }

  //    remove the current line item's item level tax from applied item level taxes array
  //    if this tax is not applied on any other line items

  if (taxesInItemBeforeUpdate.length > 0) {
    taxesInItemBeforeUpdate.forEach(taxInItemBeforeUpdate => {
      const isTaxAppliedOnOtherItem = updatedLineItemsList.find(lineItemCurr => {
        return lineItemCurr?.taxes?.find(lineItemTax => lineItemTax.id === taxInItemBeforeUpdate.id);
      });
      const isTaxAppliedOnServiceCharge = selectedServiceCharge?.taxes?.find(
        tax => tax.id === taxInItemBeforeUpdate.id
      );
      if (!isTaxAppliedOnOtherItem && !isTaxAppliedOnServiceCharge) {
        appliedLineItemLevelTaxes = appliedLineItemLevelTaxes.filter(tax => tax.id !== taxInItemBeforeUpdate.id);
      }
    });
  }

  return {
    lineItems: prepareLineItemList(updatedLineItemsList),
    ...prepareSummatedUniqueTaxList({
      appliedLineItemLevelTaxes,
      invoiceLineItems: updatedLineItemsList,
      selectedServiceCharge: selectedServiceCharge
    }),
    surchargeAmount: surchargeInCurrencyWithBps.toString(),
    isServiceFeeEnabled: currentSurchargeState.enabled
  };
};

export const getModifiedAutoFilledFields = () => {
  const { rootReducer } = store.getState();
  const { createJob } = rootReducer;
  const { autoFilledData } = createJob;

  // If no auto-filled data, return empty array
  if (!autoFilledData) return [];

  const modifiedFields = [];

  // Basic fields comparison
  if (autoFilledData.title && autoFilledData.title !== createJob.jobTitle) {
    modifiedFields.push('title');
  }

  if (autoFilledData.description && autoFilledData.description !== createJob.jobDescription) {
    modifiedFields.push('description');
  }

  if (autoFilledData.leadSource && autoFilledData.leadSource !== createJob.leadSource) {
    modifiedFields.push('leadSource');
  }

  if (autoFilledData.leadSourceType && autoFilledData.leadSourceType !== createJob.leadSourceType?.id) {
    modifiedFields.push('leadSourceType');
  }

  if (autoFilledData.note?.comment && autoFilledData.note.comment !== createJob.jobNotes) {
    modifiedFields.push('notes');
  }

  if (autoFilledData.referenceNo && autoFilledData.referenceNo !== createJob.jobReferenceNumber) {
    modifiedFields.push('referenceNumber');
  }

  if (autoFilledData.isScheduled !== undefined && autoFilledData.isScheduled !== createJob.isJobScheduled) {
    modifiedFields.push('isScheduled');
  }

  // Scheduling dates - only check if isScheduled is true
  if (autoFilledData.isScheduled) {
    if (autoFilledData.scheduledFrom && autoFilledData.scheduledFrom !== createJob.scheduledFrom) {
      modifiedFields.push('scheduledFrom');
    }
    if (autoFilledData.scheduledTo && autoFilledData.scheduledTo !== createJob.scheduledTo) {
      modifiedFields.push('scheduledTo');
    }
  }

  // External company - simplified check
  if (autoFilledData.externalCompanyId && autoFilledData.externalCompanyId !== createJob.externalCompany?.id) {
    modifiedFields.push('externalCompany');
  }

  // Customer data
  if (autoFilledData.customerLatest?.id && autoFilledData.customerLatest.id !== createJob.jobCustomer?.['id']) {
    modifiedFields.push('customer');
  }

  // Service address
  if (
    (autoFilledData.customerLatest?.serviceAddress?.[0]?.id ||
      autoFilledData.customerLatest?.serviceAddress?.[0]?.street) &&
    (autoFilledData.customerLatest.serviceAddress?.[0]?.id !== createJob.serviceAddress?.id ||
      autoFilledData.customerLatest.serviceAddress?.[0]?.street !== createJob.serviceAddress?.street)
  ) {
    modifiedFields.push('serviceAddress');
  }

  // Billing address
  if (
    autoFilledData.customerLatest?.billingAddress?.id &&
    autoFilledData.customerLatest.billingAddress.id !== createJob.billingAddress?.id
  ) {
    modifiedFields.push('billingAddress');
  }

  return modifiedFields;
};

// Returns a filtered list of job statuses based on user permissions and team configurations
export const getAvailableJobStatuses = (currentStatus: JobStatus, jobPermissions: any, teamConfigs: any) => {
  let availableStatuses = [...JobsStatuses];
  const canModifyDoneJobs = jobPermissions?.modifyDoneJobs;
  const canMarkJobDone = jobPermissions?.closeOrMarkJobAsDone;
  const canManageOthersJobs = jobPermissions?.manageOthersJobs;
  const jobInitialStatus = teamConfigs?.jobInitialStatus;
  // If current status is DONE and user can't modify done jobs
  if (currentStatus === JobStatus.DONE && !canModifyDoneJobs) {
    return [availableStatuses.find(statusItem => statusItem.value === JobStatus.DONE)];
  }
  // Remove DONE status if user doesn't have permission to mark jobs as done
  if (!canMarkJobDone) {
    availableStatuses = availableStatuses.filter(statusItem => statusItem.value !== JobStatus.DONE);
  }
  // Remove INBOX status if user doesn't have manageOthersJobs permission or jobInitialStatus is not INBOX
  if (!canManageOthersJobs || jobInitialStatus !== JobStatus.INBOX) {
    availableStatuses = availableStatuses.filter(statusItem => statusItem.value !== JobStatus.INBOX);
  }
  return availableStatuses;
};

export const JobStatusFilters = (jobPermissions, teamConfigs) => {
  let filterStatuses = [...JobsStatuses];
  const canManageOthersJobs = jobPermissions?.manageOthersJobs;
  const jobInitialStatus = teamConfigs?.jobInitialStatus;
  if (!canManageOthersJobs || jobInitialStatus !== JobStatus.INBOX) {
    filterStatuses = filterStatuses.filter(statusItem => statusItem.value !== JobStatus.INBOX);
  }
  return filterStatuses;
};

/**
 * Format job view filters for API calls
 * This utility function standardizes filter formatting across components
 */
export const formatJobViewFiltersForAPI = (currentViewFilters: any) => {
  const isSpecialDateCase = currentViewFilters.isToday || currentViewFilters.schedulePreset;

  let scheduledFromValue: string | null = '';
  let scheduledToValue: string | null = '';

  if (isSpecialDateCase) {
    scheduledFromValue = null;
    scheduledToValue = null;
  } else {
    scheduledFromValue = currentViewFilters.scheduledFrom
      ? prepareStartDateForSave(currentViewFilters.scheduledFrom as string)
      : '';
    scheduledToValue = currentViewFilters.scheduledTo
      ? prepareEndDateForSave(currentViewFilters.scheduledTo as string)
      : '';
  }

  const formattedFilters = {
    customerIds: currentViewFilters.customerIds ? (currentViewFilters.customerIds as string[]).join(',') : '',
    assignees: currentViewFilters.assignees ? (currentViewFilters.assignees as string[]).join(',') : '',
    createdBy: currentViewFilters.createdBy ? (currentViewFilters.createdBy as string[]).join(',') : '',
    status: currentViewFilters.status ? (currentViewFilters.status as string[]).join(',') : '',
    leadSource: currentViewFilters.leadSource ? (currentViewFilters.leadSource as string[]).join(',') : '',
    scheduledFrom: scheduledFromValue,
    scheduledTo: scheduledToValue,
    amount: currentViewFilters.amount ? getAmountQueryParam(currentViewFilters.amount as AmountFilter) : null,
    externalCompany: currentViewFilters.externalCompany
      ? (currentViewFilters.externalCompany as string[]).join(',')
      : '',
    isToday: currentViewFilters.isToday as boolean,
    schedulePreset: currentViewFilters.schedulePreset as string,
    tags: currentViewFilters.tags ? (currentViewFilters.tags as string[]).join(',') : '',
    noTags: currentViewFilters.noTags || null,
    isUnscheduled: currentViewFilters.isUnscheduled as boolean,
    serviceArea: currentViewFilters.serviceArea ? (currentViewFilters.serviceArea as string[]).join(',') : '',
    invoiceStatus: currentViewFilters.invoiceStatus ? (currentViewFilters.invoiceStatus as string[]).join(',') : '',
    estimateStatus: currentViewFilters.estimateStatus ? (currentViewFilters.estimateStatus as string[]).join(',') : '',
    createdFrom: currentViewFilters.createdFrom,
    createdTo: currentViewFilters.createdTo
  };

  // Filter out empty values to keep the payload clean
  return Object.entries(formattedFilters).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
};

//Jobs table actions Schedule Utils

export const scheduleLaterToday6PM = (): ScheduleTime => {
  const today = new Date();
  const scheduledFrom = set(today, { hours: 18, minutes: 0, seconds: 0, milliseconds: 0 });
  const scheduledTo = add(scheduledFrom, { minutes: 60 });

  return {
    scheduledFrom: scheduledFrom.toISOString(),
    scheduledTo: scheduledTo.toISOString()
  };
};

export const scheduleTomorrow9AM = (): ScheduleTime => {
  const tomorrow = add(new Date(), { days: 1 });
  const scheduledFrom = set(tomorrow, { hours: 9, minutes: 0, seconds: 0, milliseconds: 0 });
  const scheduledTo = add(scheduledFrom, { minutes: 60 });

  return {
    scheduledFrom: scheduledFrom.toISOString(),
    scheduledTo: scheduledTo.toISOString()
  };
};

export const scheduleNextWeekMonday9AM = (): ScheduleTime => {
  const today = new Date();
  const nextWeek = addWeeks(today, 1);
  const nextMonday = startOfWeek(nextWeek, { weekStartsOn: 1 }); // 1 = Monday
  const scheduledFrom = set(nextMonday, { hours: 9, minutes: 0, seconds: 0, milliseconds: 0 });
  const scheduledTo = add(scheduledFrom, { minutes: 60 });

  return {
    scheduledFrom: scheduledFrom.toISOString(),
    scheduledTo: scheduledTo.toISOString()
  };
};

export const getAmountParam = (amt: AmountFilter) => {
  if (!amt || !amt.filterOperation) return null;
  const { filterOperation, minAmount, maxAmount } = amt;
  if (filterOperation === AmountFilterOperation.ALL) return null;
  if (filterOperation === AmountFilterOperation.BW) return `${filterOperation}:${minAmount}:${maxAmount}`;
  return `${filterOperation}:${minAmount}`;
};
